import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, Minus } from "lucide-react";
import { PROGRAMS } from "../data/content";

// distinct therapeutic hues to give each program its own identity
const HUES = {
  "drumtalk": "#548a88",
  "percussion-pathways": "#b89556",
  "percussion-pals": "#c46a58",
  "trap-learning": "#5b7a99",
};

// dedicated landing pages have clean URLs; others fall back to /program/:slug
const ROUTES = {
  "drumtalk": "/drumtalk",
  "percussion-pathways": "/percussion-pathways",
  "percussion-pals": "/percussion-pals",
};

export default function Programs() {
  const [open, setOpen] = useState("drumtalk");
  return (
    <section
      id="programs"
      className="relative py-24 md:py-32 px-6 md:px-10 border-t border-white/5 overflow-hidden"
      data-testid="programs-section"
    >
      {/* --- decorative pattern & shape layers --- */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {/* dotted grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.3]"
          style={{
            backgroundImage: "radial-gradient(rgba(84,138,136,0.28) 1px, transparent 1.4px)",
            backgroundSize: "28px 28px",
            maskImage: "radial-gradient(80% 70% at 50% 40%, black, transparent 78%)",
            WebkitMaskImage: "radial-gradient(80% 70% at 50% 40%, black, transparent 78%)",
          }}
        />
        {/* floating therapeutic blobs */}
        <div
          className="float-blob absolute -top-16 -right-10 w-[26rem] h-[26rem] rounded-full blur-[110px] opacity-30"
          style={{ background: "radial-gradient(circle, rgba(84,138,136,0.4), transparent 70%)" }}
        />
        <div
          className="float-blob-slow absolute top-1/3 -left-24 w-[30rem] h-[30rem] rounded-full blur-[130px] opacity-25"
          style={{ background: "radial-gradient(circle, rgba(184,149,86,0.35), transparent 70%)" }}
        />
        <div
          className="float-blob absolute bottom-0 right-1/4 w-[22rem] h-[22rem] rounded-full blur-[120px] opacity-20"
          style={{ background: "radial-gradient(circle, rgba(107,143,113,0.4), transparent 70%)" }}
        />
        {/* slow rotating ring shape */}
        <div className="spin-slow absolute -bottom-40 -right-40 w-[34rem] h-[34rem] rounded-full border border-primary-c/15" />
        <div className="spin-slow absolute -bottom-28 -right-28 w-[26rem] h-[26rem] rounded-full border border-primary-c/10" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-primary-c/70" />
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-primary-c">The Four Core Avenues</p>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.04]">
            Gentle programs,{" "}
            <span
              className="italic"
              style={{
                background: "linear-gradient(100deg, #7fb0ad, #548a88 40%, #b89556 75%, #6b8f71)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              tuned to the room.
            </span>
          </h2>
        </motion.div>

        <div className="mt-14">
          {PROGRAMS.map((prog, i) => {
            const isOpen = open === prog.slug;
            const hue = HUES[prog.slug] || "#548a88";
            return (
              <motion.div
                key={prog.slug}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="group/row relative border-b border-white/10"
                style={{ "--row-hue": hue }}
                data-testid={`program-${prog.slug}`}
              >
                {/* left accent bar that grows on open/hover */}
                <span
                  className="absolute left-0 top-0 h-full w-[3px] origin-top scale-y-0 group-hover/row:scale-y-100 transition-transform duration-500"
                  style={{ backgroundColor: hue, transform: isOpen ? "scaleY(1)" : undefined, boxShadow: `0 0 20px ${hue}` }}
                />
                <button
                  onClick={() => setOpen(isOpen ? "" : prog.slug)}
                  data-testid={`program-toggle-${prog.slug}`}
                  className="w-full py-8 md:py-10 flex items-center gap-5 md:gap-6 text-left pl-4 md:pl-6"
                >
                  <span
                    className="shrink-0 grid place-items-center h-11 w-11 md:h-14 md:w-14 rounded-2xl font-mono text-base md:text-lg border transition-all duration-300 group-hover/row:scale-110"
                    style={{
                      color: hue,
                      backgroundColor: hue + "1A",
                      borderColor: (isOpen ? hue + "88" : hue + "44"),
                      boxShadow: isOpen ? `0 0 24px ${hue}55` : "none",
                    }}
                  >
                    {prog.index}
                  </span>
                  <div className="flex-1">
                    <h3
                      className="font-serif text-2xl md:text-4xl tracking-tight transition-colors duration-300"
                      style={{ color: isOpen ? hue : undefined }}
                    >
                      <span className="group-hover/row:[color:var(--row-hue)] transition-colors duration-300">{prog.name}</span>
                    </h3>
                    <p className="font-mono text-[10px] md:text-xs tracking-[0.2em] uppercase text-fg/40 mt-2">{prog.label}</p>
                  </div>
                  <span
                    className="shrink-0 grid place-items-center h-9 w-9 rounded-full border transition-all duration-300"
                    style={{
                      color: isOpen ? hue : "rgba(245,240,255,0.5)",
                      borderColor: isOpen ? hue + "66" : "rgba(255,255,255,0.12)",
                    }}
                  >
                    {isOpen ? <Minus size={20} /> : <Plus size={20} />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="pb-10 pl-4 md:pl-20 grid grid-cols-1 md:grid-cols-3 gap-8">
                        <p className="md:col-span-2 text-fg/70 leading-relaxed md:text-lg">{prog.short}</p>
                        <div className="flex flex-col justify-between gap-6">
                          {prog.logo && (
                            <img
                              src={prog.logo}
                              alt={prog.name}
                              data-testid={`program-logo-${prog.slug}`}
                              className={`h-16 w-16 md:h-20 md:w-20 rounded-2xl object-contain ${prog.logoLight ? "bg-white/90 p-1.5" : ""}`}
                            />
                          )}
                          <div>
                            <div className="font-serif text-4xl" style={{ color: hue }}>{prog.stat.value}</div>
                            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50 mt-1">{prog.stat.label}</div>
                          </div>
                          <Link
                            to={ROUTES[prog.slug] || `/program/${prog.slug}`}
                            data-testid={`program-learn-${prog.slug}`}
                            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase transition-colors"
                            style={{ color: hue }}
                          >
                            Learn more <ArrowRight size={15} />
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
